import { useState } from "react";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES, ITEMS_PER_PAGE } from "../../../constant/constant";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {object} NotificationMedia
 * @property {string} name - File name
 * @property {string} path - File path
 */

/**
 * @typedef {object} SentNotificationItem
 * @property {number} id - Notification ID
 * @property {string} scope - Scope (e.g. "tenant")
 * @property {string} tenant_id - Tenant ID
 * @property {number} institution_id - Institution ID
 * @property {number} created_by - Creator user ID
 * @property {string} created_by_role - Creator role (e.g. "organization-admin")
 * @property {number} receiver_id - Receiver user ID
 * @property {string} receiver_type - Receiver model type
 * @property {string} title - Notification title
 * @property {string} description - Notification description
 * @property {string} category - Category (e.g. "manual")
 * @property {string} event_key - Event key (e.g. "announcement")
 * @property {number|null} reference_id - Reference ID
 * @property {string|null} reference_type - Reference type
 * @property {NotificationMedia[]} media - Array of attached media files
 * @property {string|null} href - Link URL
 * @property {*} data - Additional data
 * @property {string|null} seen_at - Seen timestamp
 * @property {string|null} read_at - Read timestamp
 * @property {string|null} dismissed_at - Dismissed timestamp
 * @property {string} created_at - Created timestamp
 * @property {string} updated_at - Updated timestamp
 */

/**
 * @typedef {import("../../../common/types/types").PaginationMeta} PaginationMeta
 */

/**
 * @typedef {object} GetSentNotificationParams
 * @property {number} [page] - Page number for pagination
 * @property {number} [per_page] - Number of items per page
 */

/**
 * Custom hook to fetch sent notification list with pagination
 */
export const useGetSchoolNotificationList = () => {
  const [notifications, setNotifications] = useState(
    /** @type {SentNotificationItem[] | null | undefined} */ (undefined),
  );
  const [meta, setMeta] = useState(
    /** @type {PaginationMeta | null | undefined} */ (undefined),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const { axiosInstance } = useAxios();

  /**
   * Fetch sent notifications with pagination
   * @async
   * @param {GetSentNotificationParams} params
   * @returns {Promise<void>}
   */
  const getSentNotifications = async ({
    page = 1,
    per_page = ITEMS_PER_PAGE,
    filters = {},
    all_info,
  } = {}) => {
    try {
      setLoading(true);
      setError("");

      const query = new URLSearchParams();
      query.append("page", page);
      query.append("per_page", per_page);

      // ✅ add all_info if provided
      if (all_info) {
        query.append("all_info", all_info);
      }

      // ✅ add dynamic filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          query.append(`filter[${key}]`, value);
        }
      });

      const url = `${TENANT_DOMAIN}/announcements/sent?${query.toString()}`;

      const response = await axiosInstance.get(url);

      setNotifications(response.data?.data || []);
      setMeta(response.data?.meta || null);
    } catch (err) {
      setNotifications(null);
      setMeta(null);

      if (err?.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    notifications,
    meta,
    loading,
    error,
    getSentNotifications,
  };
};
